import { useMutation, useQuery, useQueryClient, UseQueryOptions, QueryClient } from "react-query";
import { useTetherToast } from "@tether-web-portal/components/toast/useTetherToast";
import { api } from "../api";
import { getApiErrorMessage } from "../api-types";
import { QueryKeys as PropertyQueryKeys } from "../properties/hooks";
import {
  AssignmentsResult,
  BillableUtility,
  CreatePropertyTenantInput,
  EndDateMutation,
  PropertyTenant,
  PropertyTenantLocationAssignment,
  PropertyTenantRate,
  SetRateInput,
  TenantAssignmentDetail,
  UpdatePropertyTenantInput,
} from "../../types/PropertyTenant";

export enum PropertyTenantQueryKeys {
  TENANTS = "property-tenants",
  TENANT = "property-tenant",
  RATES = "property-tenant-rates",
  ASSIGNMENTS = "property-tenant-assignments",
}

/**
 * Invalidate the location-detail list views that surface tenant assignments
 * (outsides + circuit-nodes) plus the tenant list, after an assignment change.
 * Uses the real property query keys: QueryKeys.OUTSIDE ("outside") and
 * QueryKeys.CIRCUITNODES ("circuit-nodes").
 */
const invalidateLocationViews = (queryClient: QueryClient, propertyId: string) => {
  queryClient.invalidateQueries([PropertyQueryKeys.OUTSIDE, propertyId]);
  queryClient.invalidateQueries([PropertyQueryKeys.CIRCUITNODES, propertyId]);
  // The Outside & Water tab's tenant chip reads `activeTenant` off the property
  // detail (propertyInfo) query, keyed [PROPERTY, propertyId, ...] — NOT the
  // [OUTSIDE, …] key. Invalidate by the [PROPERTY, propertyId] prefix so the
  // chip there refreshes immediately after an assignment/rename/recolour, rather
  // than staying stale until a manual page reload.
  queryClient.invalidateQueries([PropertyQueryKeys.PROPERTY, propertyId]);
  queryClient.invalidateQueries([PropertyTenantQueryKeys.TENANTS, propertyId]);
  // The assignments query is keyed by propertyTenantId (not propertyId), so we
  // invalidate by prefix to refresh whichever tenant's metering tab is open.
  // refetchInactive is required here: when a tenant is assigned from the Areas
  // page the Metering tab is unmounted, so its [ASSIGNMENTS, tenantId] query is
  // inactive. With the global refetchOnMount:false (pages/App.tsx) +
  // staleTime:Infinity, an inactive+stale query is NOT refetched on remount, so
  // it would show stale data until a full page reload. refetchInactive:true
  // forces the invalidated inactive query to refetch so the Metering tab is fresh
  // when it next mounts. (react-query v3 — invalidateQueries(queryKey, filters)
  // accepts refetchInactive.)
  queryClient.invalidateQueries([PropertyTenantQueryKeys.ASSIGNMENTS], { refetchInactive: true });
};

// ---------------------------------------------------------------------------
// Tenants
// ---------------------------------------------------------------------------

export const useGetPropertyTenants = (
  propertyId: string,
  options?: UseQueryOptions<PropertyTenant[], Error> & { includeArchived?: boolean },
) => {
  const { includeArchived, ...queryOptions } = options ?? {};
  return useQuery<PropertyTenant[], Error>(
    // includeArchived is part of the key so toggling refetches; the existing
    // [TENANTS, propertyId] invalidations still match by prefix (react-query v3).
    [PropertyTenantQueryKeys.TENANTS, propertyId, Boolean(includeArchived)],
    () =>
      api.get("v2/propertytenant", {
        propertyId,
        // Only send the param when true so the default (excludes archived) stays clean.
        ...(includeArchived ? { includeArchived: "true" } : {}),
      }),
    {
      enabled: Boolean(propertyId),
      staleTime: Infinity,
      ...queryOptions,
    },
  );
};

export const useGetPropertyTenant = (id: string, options?: UseQueryOptions<PropertyTenant, Error>) =>
  useQuery<PropertyTenant, Error>(
    [PropertyTenantQueryKeys.TENANT, id],
    () => api.get(`v2/propertytenant/${id}`),
    {
      enabled: Boolean(id),
      staleTime: Infinity,
      ...options,
    },
  );

export const useGetTenantAssignments = (
  propertyTenantId: string,
  options?: UseQueryOptions<TenantAssignmentDetail[], Error>,
) =>
  useQuery<TenantAssignmentDetail[], Error>(
    [PropertyTenantQueryKeys.ASSIGNMENTS, propertyTenantId],
    () => api.get(`v2/propertytenant/${propertyTenantId}/assignments`),
    {
      enabled: Boolean(propertyTenantId),
      staleTime: Infinity,
      ...options,
    },
  );

export const useCreatePropertyTenantMutation = (
  propertyId: string,
  onSuccess?: (tenant: PropertyTenant) => void,
) => {
  const queryClient = useQueryClient();
  const { errorToast, successToast } = useTetherToast();

  const handleCreate = async ({ input }: { input: CreatePropertyTenantInput }) =>
    api.post(`v2/propertytenant?propertyId=${propertyId}`, input);

  return useMutation(handleCreate, {
    onError: (e: Error) => {
      errorToast({
        title: "Unable to add tenant",
        description: getApiErrorMessage(e),
      });
    },
    onSuccess: (tenant: PropertyTenant) => {
      successToast({
        title: "Tenant Added",
      });
      queryClient.invalidateQueries([PropertyTenantQueryKeys.TENANTS, propertyId]);
      if (onSuccess) {
        onSuccess(tenant);
      }
    },
  });
};

export const useUpdatePropertyTenantMutation = (propertyId: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { errorToast, successToast } = useTetherToast();

  const handleUpdate = async ({ id, input }: { id: string; input: UpdatePropertyTenantInput }) =>
    api.patch(`v2/propertytenant/${id}`, input);

  return useMutation(handleUpdate, {
    onError: (e: Error) => {
      errorToast({
        title: "Unable to update tenant",
        description: getApiErrorMessage(e),
      });
    },
    onSuccess: (data, { id }) => {
      successToast({
        title: "Tenant Updated",
      });
      queryClient.invalidateQueries([PropertyTenantQueryKeys.TENANT, id]);
      // A rename/recolour/date change is embedded in the cached `activeTenant`
      // summary on the Areas (Energy + Outside) location views, so refresh those
      // too — otherwise the chips there keep the old name/colour until reload.
      invalidateLocationViews(queryClient, propertyId);
      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

export const useDeletePropertyTenantMutation = (propertyId: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { errorToast, successToast } = useTetherToast();

  const handleDelete = async ({ id }: { id: string }) => api.del(`v2/propertytenant/${id}`);

  return useMutation(handleDelete, {
    onError: (e: Error) => {
      errorToast({
        title: "Unable to remove tenant",
        description: getApiErrorMessage(e),
      });
    },
    onSuccess: (data, { id }) => {
      successToast({
        title: "Tenant Removed",
      });
      queryClient.invalidateQueries([PropertyTenantQueryKeys.TENANTS, propertyId]);
      queryClient.invalidateQueries([PropertyTenantQueryKeys.TENANT, id]);
      // Refresh the location-detail views that embed the tenant badge so they
      // don't show the removed tenant under staleTime: Infinity.
      invalidateLocationViews(queryClient, propertyId);
      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

export const useArchiveTenantMutation = (propertyId: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { errorToast, successToast } = useTetherToast();

  const handleArchive = async ({ id }: { id: string }) =>
    api.post<PropertyTenant>(`v2/propertytenant/${id}/archive`);

  return useMutation(handleArchive, {
    onError: (e: Error) => {
      errorToast({
        title: "Unable to archive tenant",
        description: getApiErrorMessage(e),
      });
    },
    onSuccess: (data, { id }) => {
      successToast({
        title: "Tenant Archived",
      });
      queryClient.invalidateQueries([PropertyTenantQueryKeys.TENANTS, propertyId]);
      queryClient.invalidateQueries([PropertyTenantQueryKeys.TENANT, id]);
      // Refresh the location-detail views that embed the tenant badge so they
      // don't show the archived tenant under staleTime: Infinity.
      invalidateLocationViews(queryClient, propertyId);
      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

export const useRestoreTenantMutation = (propertyId: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { errorToast, successToast } = useTetherToast();

  const handleRestore = async ({ id }: { id: string }) =>
    api.post<PropertyTenant>(`v2/propertytenant/${id}/restore`);

  return useMutation(handleRestore, {
    onError: (e: Error) => {
      errorToast({
        title: "Unable to restore tenant",
        description: getApiErrorMessage(e),
      });
    },
    onSuccess: (data, { id }) => {
      successToast({
        title: "Tenant Restored",
      });
      queryClient.invalidateQueries([PropertyTenantQueryKeys.TENANTS, propertyId]);
      queryClient.invalidateQueries([PropertyTenantQueryKeys.TENANT, id]);
      // Refresh the location-detail views that embed the tenant badge so they
      // reflect the restored tenant under staleTime: Infinity.
      invalidateLocationViews(queryClient, propertyId);
      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

// ---------------------------------------------------------------------------
// Rates
// ---------------------------------------------------------------------------

export const useGetPropertyTenantRates = (
  propertyTenantId: string,
  options?: UseQueryOptions<PropertyTenantRate[], Error>,
) =>
  useQuery<PropertyTenantRate[], Error>(
    [PropertyTenantQueryKeys.RATES, propertyTenantId],
    () => api.get("v2/propertytenantrate", { propertyTenantId }),
    {
      enabled: Boolean(propertyTenantId),
      staleTime: Infinity,
      ...options,
    },
  );

export const useSetPropertyTenantRateMutation = (propertyTenantId: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { errorToast, successToast } = useTetherToast();

  const handleSetRate = async ({ utility, input }: { utility: BillableUtility; input: SetRateInput }) =>
    api.put("v2/propertytenantrate", input, { propertyTenantId, utility });

  return useMutation(handleSetRate, {
    onError: (e: Error) => {
      errorToast({
        title: "Unable to set rate",
        description: getApiErrorMessage(e),
      });
    },
    onSuccess: () => {
      successToast({
        title: "Rate Updated",
      });
      queryClient.invalidateQueries([PropertyTenantQueryKeys.RATES, propertyTenantId]);
      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

export const useClearPropertyTenantRateMutation = (propertyTenantId: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { errorToast, successToast } = useTetherToast();

  const handleClearRate = async ({ utility }: { utility: BillableUtility }) =>
    api.del("v2/propertytenantrate", undefined, { propertyTenantId, utility });

  return useMutation(handleClearRate, {
    onError: (e: Error) => {
      errorToast({
        title: "Unable to clear rate",
        description: getApiErrorMessage(e),
      });
    },
    onSuccess: () => {
      successToast({
        title: "Rate Cleared",
      });
      queryClient.invalidateQueries([PropertyTenantQueryKeys.RATES, propertyTenantId]);
      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

// ---------------------------------------------------------------------------
// Outside assignments
// ---------------------------------------------------------------------------

export interface AttachOutsideInput {
  propertyTenantId: string;
  outsideId: string;
  startDate: string;
  endDate?: EndDateMutation;
}

export const useAttachOutsideAssignmentMutation = (propertyId: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { errorToast, successToast } = useTetherToast();

  const handleAttach = async ({ input }: { input: AttachOutsideInput }) =>
    api.post<AssignmentsResult>("v2/propertytenantoutsideassignment", input);

  return useMutation(handleAttach, {
    onError: (e: Error) => {
      errorToast({
        title: "Unable to assign tenant",
        description: getApiErrorMessage(e),
      });
    },
    onSuccess: () => {
      successToast({
        title: "Tenant Assigned",
      });
      invalidateLocationViews(queryClient, propertyId);
      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

export interface ChangeOutsideInput {
  outsideId: string;
  newPropertyTenantId: string;
  changeDate: string;
  newEndDate?: EndDateMutation;
}

export const useChangeOutsideAssignmentMutation = (propertyId: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { errorToast, successToast } = useTetherToast();

  const handleChange = async ({ input }: { input: ChangeOutsideInput }) =>
    api.post<AssignmentsResult>("v2/propertytenantoutsideassignment/change", input);

  return useMutation(handleChange, {
    onError: (e: Error) => {
      errorToast({
        title: "Unable to change tenant",
        description: getApiErrorMessage(e),
      });
    },
    onSuccess: () => {
      successToast({
        title: "Tenant Changed",
      });
      invalidateLocationViews(queryClient, propertyId);
      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

export interface DetachOutsideInput {
  outsideId: string;
  endDate?: string;
}

export const useDetachOutsideAssignmentMutation = (propertyId: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { errorToast, successToast } = useTetherToast();

  const handleDetach = async ({ input }: { input: DetachOutsideInput }) =>
    api.del<void>("v2/propertytenantoutsideassignment", undefined, {
      outsideId: input.outsideId,
      ...(input.endDate && { endDate: input.endDate }),
    });

  return useMutation(handleDetach, {
    onError: (e: Error) => {
      errorToast({
        title: "Unable to unassign tenant",
        description: getApiErrorMessage(e),
      });
    },
    onSuccess: () => {
      successToast({
        title: "Tenant Unassigned",
      });
      invalidateLocationViews(queryClient, propertyId);
      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

export interface EditOutsideAssignmentDatesInput {
  outsideId: string;
  startDate?: string;
  endDate?: EndDateMutation;
}

export const useEditOutsideAssignmentDatesMutation = (propertyId: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { errorToast, successToast } = useTetherToast();

  const handleEdit = async ({ input }: { input: EditOutsideAssignmentDatesInput }) =>
    api.patch<AssignmentsResult>("v2/propertytenantoutsideassignment", input);

  return useMutation(handleEdit, {
    onError: (e: Error) => {
      errorToast({
        title: "Unable to update assignment",
        description: getApiErrorMessage(e),
      });
    },
    onSuccess: () => {
      successToast({
        title: "Assignment Updated",
      });
      invalidateLocationViews(queryClient, propertyId);
      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

// ---------------------------------------------------------------------------
// Circuit-node assignments
// ---------------------------------------------------------------------------

export interface AttachCircuitNodeInput {
  propertyTenantId: string;
  circuitNodeId: string;
  startDate: string;
  endDate?: EndDateMutation;
}

export const useAttachCircuitNodeAssignmentMutation = (propertyId: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { errorToast, successToast } = useTetherToast();

  const handleAttach = async ({ input }: { input: AttachCircuitNodeInput }) =>
    api.post<AssignmentsResult>("v2/propertytenantcircuitnodeassignment", input);

  return useMutation(handleAttach, {
    onError: (e: Error) => {
      errorToast({
        title: "Unable to assign tenant",
        description: getApiErrorMessage(e),
      });
    },
    onSuccess: () => {
      successToast({
        title: "Tenant Assigned",
      });
      invalidateLocationViews(queryClient, propertyId);
      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

export interface ChangeCircuitNodeInput {
  circuitNodeId: string;
  newPropertyTenantId: string;
  changeDate: string;
  newEndDate?: EndDateMutation;
}

export const useChangeCircuitNodeAssignmentMutation = (propertyId: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { errorToast, successToast } = useTetherToast();

  const handleChange = async ({ input }: { input: ChangeCircuitNodeInput }) =>
    api.post<AssignmentsResult>("v2/propertytenantcircuitnodeassignment/change", input);

  return useMutation(handleChange, {
    onError: (e: Error) => {
      errorToast({
        title: "Unable to change tenant",
        description: getApiErrorMessage(e),
      });
    },
    onSuccess: () => {
      successToast({
        title: "Tenant Changed",
      });
      invalidateLocationViews(queryClient, propertyId);
      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

export interface DetachCircuitNodeInput {
  circuitNodeId: string;
  endDate?: string;
}

export const useDetachCircuitNodeAssignmentMutation = (propertyId: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { errorToast, successToast } = useTetherToast();

  const handleDetach = async ({ input }: { input: DetachCircuitNodeInput }) =>
    api.del<void>("v2/propertytenantcircuitnodeassignment", undefined, {
      circuitNodeId: input.circuitNodeId,
      ...(input.endDate && { endDate: input.endDate }),
    });

  return useMutation(handleDetach, {
    onError: (e: Error) => {
      errorToast({
        title: "Unable to unassign tenant",
        description: getApiErrorMessage(e),
      });
    },
    onSuccess: () => {
      successToast({
        title: "Tenant Unassigned",
      });
      invalidateLocationViews(queryClient, propertyId);
      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

export interface EditCircuitNodeAssignmentDatesInput {
  circuitNodeId: string;
  startDate?: string;
  endDate?: EndDateMutation;
}

export const useEditCircuitNodeAssignmentDatesMutation = (propertyId: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { errorToast, successToast } = useTetherToast();

  const handleEdit = async ({ input }: { input: EditCircuitNodeAssignmentDatesInput }) =>
    api.patch<AssignmentsResult>("v2/propertytenantcircuitnodeassignment", input);

  return useMutation(handleEdit, {
    onError: (e: Error) => {
      errorToast({
        title: "Unable to update assignment",
        description: getApiErrorMessage(e),
      });
    },
    onSuccess: () => {
      successToast({
        title: "Assignment Updated",
      });
      invalidateLocationViews(queryClient, propertyId);
      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

// ---------------------------------------------------------------------------
// Edit a single assignment by id (incl. historic/past ones)
// ---------------------------------------------------------------------------

export interface EditAssignmentDatesInput {
  startDate?: string;
  endDate?: EndDateMutation;
}

/**
 * Edit the dates of ONE assignment, addressed by its id — unlike the
 * Outside/CircuitNode edit hooks, which resolve the currently-active assignment
 * by target. Lets the Metering tab edit a HISTORIC (past) assignment.
 *
 * Keyed by propertyTenantId so it can refresh the open Metering tab's
 * assignments query directly; `propertyId` (when known) additionally refreshes
 * the location-detail + tenant-list views the sibling edit hooks invalidate.
 */
export const useEditAssignmentDatesMutation = (
  propertyTenantId: string,
  propertyId: string | undefined,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient();
  const { errorToast, successToast } = useTetherToast();

  const handleEdit = async ({ id, input }: { id: string; input: EditAssignmentDatesInput }) =>
    api.patch<PropertyTenantLocationAssignment>(`v2/propertytenantassignment/${id}`, input);

  return useMutation(handleEdit, {
    onError: (e: Error) => {
      errorToast({
        title: "Unable to update assignment",
        description: getApiErrorMessage(e),
      });
    },
    onSuccess: () => {
      successToast({
        title: "Assignment Updated",
      });
      // The Metering tab's assignments query is keyed by propertyTenantId.
      queryClient.invalidateQueries([PropertyTenantQueryKeys.ASSIGNMENTS, propertyTenantId]);
      // Plus the location-detail + tenant-list views (and the assignments prefix)
      // the sibling edit hooks refresh — only when the property is known.
      if (propertyId) {
        invalidateLocationViews(queryClient, propertyId);
      }
      if (onSuccess) {
        onSuccess();
      }
    },
  });
};
